import * as z from "zod";
import { flag, pagination, uuidV7, version } from "../index";
import { emailAssetId } from "./email-asset";

// The brand kit (ADR-287 §3, ADR-293): the colours, the font and the logo an email of a brand is drawn with, one per
// brand plus the one of Tomorrow. A document names its tokens, never a colour, so a brand that changes its colours
// changes every draft, and a published version keeps the kit it was published with.

export const kitColourRoles = [
  "page",
  "surface",
  "text",
  "muted",
  "border",
  "primary",
  "onPrimary",
  "accent",
  "onAccent",
] as const;
export type KitColourRole = (typeof kitColourRoles)[number];

const HEX = /^#[0-9a-fA-F]{6}$/;
/** A colour of a kit, `#rrggbb`: what the editor accepts as a colour while it is being typed. */
export const isKitColour = (value: string) => HEX.test(value);
const hex = z.string().regex(HEX, "A colour as #rrggbb");
export const kitPalette = z.object({
  /** Around the card, under the footer, and the sections with `page`. */
  page: hex,
  /** The card. */
  surface: hex,
  text: hex,
  muted: hex,
  border: hex,
  primary: hex,
  onPrimary: hex,
  accent: hex,
  onAccent: hex,
});
export type KitPalette = z.output<typeof kitPalette>;

/** System fonts only, as ADR-268: no web font to load and no address to keep. */
export const kitFonts = ["sans", "serif"] as const;
export type KitFont = (typeof kitFonts)[number];

/** The pairs a text is read on, and the contrast they need (WCAG 1.4.3, 4.5:1 for normal text). */
export const kitContrastPairs = [
  ["text", "surface"],
  ["muted", "surface"],
  ["text", "page"],
  // the footer, around the card
  ["muted", "page"],
  // the label of an outline button, on the card and on a section with `page`
  ["primary", "surface"],
  ["primary", "page"],
  ["onPrimary", "primary"],
  ["onAccent", "accent"],
] as const satisfies readonly (readonly [KitColourRole, KitColourRole])[];
export const KIT_MIN_CONTRAST = 4.5;

/** The relative luminance of a `#rrggbb` (WCAG 2.2). */
export function luminance(colour: string): number {
  const channel = (offset: number) => {
    const c = Number.parseInt(colour.slice(offset, offset + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** From 1 to 21, whatever the order. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

export interface PairContrast {
  fore: KitColourRole;
  back: KitColourRole;
  /** Rounded to two decimals. */
  ratio: number;
  passes: boolean;
}

/** Every pair of a palette a text is read on, with its contrast: what the save checks and the editor shows. */
export function pairContrasts(palette: KitPalette): PairContrast[] {
  return kitContrastPairs.map(([fore, back]) => {
    const ratio = Math.round(contrastRatio(palette[fore], palette[back]) * 100) / 100;
    return { fore, back, ratio, passes: ratio >= KIT_MIN_CONTRAST };
  });
}

/** The pairs of a palette below `KIT_MIN_CONTRAST`, as `text/surface`, with their ratio rounded to two decimals. */
export function lowContrast(palette: KitPalette): { pair: string; ratio: number }[] {
  return pairContrasts(palette).flatMap(({ fore, back, ratio, passes }) =>
    passes ? [] : [{ pair: `${fore}/${back}`, ratio }],
  );
}

const name = z.string().trim().min(1).max(120);
/** `null` is the kit of Tomorrow. */
const brandOrgId = z.uuid().nullable();

export const brandKitDto = z
  .object({
    id: z.uuid(),
    brandOrgId,
    name: z.string(),
    light: kitPalette,
    dark: kitPalette,
    font: z.enum(kitFonts),
    logoAssetId: emailAssetId.nullable(),
    /** The logo on crm-images, for the screens (ADR-295). */
    logoUrl: z.url().nullable(),
    updatedAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "BrandKit" });
export type BrandKitDto = z.output<typeof brandKitDto>;

export const listBrandKitsInput = z.object({
  /** Only the kit of this brand; with `tomorrow`, only the one of Tomorrow. */
  brandOrgId: z.uuid().optional(),
  tomorrow: flag.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListBrandKitsInput = z.input<typeof listBrandKitsInput>;

export const getBrandKitInput = z.object({ brandKitId: z.uuid() });
export type GetBrandKitInput = z.input<typeof getBrandKitInput>;

/** The id comes from the client (ADR-046); one kit per brand, and one for Tomorrow. */
export const createBrandKitInput = z.object({
  id: uuidV7,
  brandOrgId,
  name,
  light: kitPalette,
  dark: kitPalette,
  font: z.enum(kitFonts).default("sans"),
  logoAssetId: emailAssetId.nullable().optional(),
});
export type CreateBrandKitInput = z.input<typeof createBrandKitInput>;

/** PATCH: an absent field stays as it is. */
export const updateBrandKitInput = z.object({
  brandKitId: z.uuid(),
  version,
  name: name.optional(),
  light: kitPalette.optional(),
  dark: kitPalette.optional(),
  font: z.enum(kitFonts).optional(),
  logoAssetId: emailAssetId.nullable().optional(),
});
export type UpdateBrandKitInput = z.input<typeof updateBrandKitInput>;
export const updateBrandKitBody = updateBrandKitInput.omit({ brandKitId: true });
